import React, { useState } from 'react';
import {
  Box,
  Button,
  Image,
  Text,
  useToast,
  Checkbox,
  VStack,
  useDisclosure,
  Link,
} from '@chakra-ui/react';
import {
  GoogleAuthProvider,
  signInWithPopup,
  browserPopupRedirectResolver,
} from 'firebase/auth';
import { auth } from '../firebase';
import LegalModal from '../components/LegalModal';

function SignIn() {
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  const handleGoogleSignIn = async () => {
    if (!agreed) {
      toast({
        title: 'Agreement required',
        description: 'Please agree to the Terms of Use and Privacy Policy to continue.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    setIsSigningIn(true);
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: 'select_account' });

    try {
      const result = await signInWithPopup(auth, provider, browserPopupRedirectResolver);
      const user = result.user;
      
      const emailDomain = user.email.split('@')[1];
      if (emailDomain !== 'mlb.com') {
        console.warn('SignIn: Unauthorized domain detected.');
        await auth.signOut();
        toast({
          title: 'Sign in failed',
          description: 'Unauthorized account. Please contact your administrator.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } else {
        toast({
          title: 'Sign in successful',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('SignIn: Popup error:', error);
      if (error.code === 'auth/popup-closed-by-user' || error.message.includes('Cross-Origin-Opener-Policy')) {
        toast({
          title: 'Sign in issue',
          description: 'The popup was blocked or closed unexpectedly. Please check browser privacy settings.',
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
      } else {
        toast({
          title: 'Sign in failed',
          description: 'Unauthorized account. Please contact your administrator.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } finally {
      setIsSigningIn(false);
    }
  };

  return (
    <Box
      textAlign="center"
      minHeight="100vh"
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      bg="brand.white"
      p={4}
    >
      <Box
        p="10"
        maxW="md"
        w="100%"
        borderWidth="2px"
        borderColor="brand.navy"
        borderRadius="none"
        boxShadow="2xl"
        bg="white"
        overflow="hidden"
      >
        <Box mb="6">
          <Image src="/logo192.png" alt="Logo" mx="auto" boxSize="150px" />
        </Box>
        <Text fontSize="md" mb="8" color="brand.navy" fontWeight="bold" textTransform="uppercase" letterSpacing="widest">
          Sign in with your GCM account
        </Text>
        
        <VStack spacing={8} align="stretch">
          <Checkbox 
            colorScheme="red" 
            isChecked={agreed} 
            onChange={(e) => {
              setAgreed(e.target.checked);
            }}
            borderColor="brand.navy"
            alignItems="start"
          >
            <Text fontSize="xs" color="brand.navy" textAlign="left" fontWeight="bold" mt="-1px" lineHeight="short">
              I agree to the{' '}
              <Link color="brand.red" textDecoration="underline" onClick={(e) => { e.preventDefault(); onOpen(); }}>
                Terms of Use and Privacy Policy
              </Link>
            </Text>
          </Checkbox>

          <Button
            variant="solid"
            onClick={handleGoogleSignIn}
            isDisabled={isSigningIn || !agreed}
            w="100%"
            py="8"
            isLoading={isSigningIn}
            fontSize="lg"
            textTransform="uppercase"
            letterSpacing="2px"
            bg="brand.red"
            color="white"
            _hover={{ bg: "brand.navy" }}
            _active={{ bg: "brand.navy" }}
          >
            Sign in
          </Button>
        </VStack>
      </Box>
      <LegalModal isOpen={isOpen} onClose={onClose} />
    </Box>
  );
}

export default SignIn;